"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.configureEmbeddedHTMLSupport = void 0;
const react_1 = __importStar(require("react"));
const dompurify_1 = require("./dompurify");
const html_react_parser_1 = __importStar(require("html-react-parser"));
// DOMPurify strips target attributes from anchor tags by default, so we need to add a hook to preserve them.
// If the anchor tag has a target attribute, we add a temporary attribute to preserve the value while sanitizing.
// After sanitizing, we restore the target attribute and add the 'noopener' rel attribute if the target is '_blank'.
const DOMPurify = (0, dompurify_1.resolveDOMPurify)();
const TEMPORARY_ATTRIBUTE = 'data-temp-href-target';
DOMPurify.addHook('beforeSanitizeAttributes', function (nodeFromHook) {
    var _a;
    const node = nodeFromHook;
    if (node.tagName === 'A') {
        if (node.hasAttribute('target')) {
            node.setAttribute(TEMPORARY_ATTRIBUTE, (_a = node.getAttribute('target')) !== null && _a !== void 0 ? _a : '');
        }
    }
});
DOMPurify.addHook('afterSanitizeAttributes', (nodeFromHook) => {
    var _a;
    const node = nodeFromHook;
    if (node.tagName === 'A' && node.hasAttribute(TEMPORARY_ATTRIBUTE)) {
        node.setAttribute('target', (_a = node.getAttribute(TEMPORARY_ATTRIBUTE)) !== null && _a !== void 0 ? _a : '');
        node.removeAttribute(TEMPORARY_ATTRIBUTE);
        if (node.getAttribute('target') === '_blank') {
            node.setAttribute('rel', 'noopener noreferrer');
        }
    }
});
const toSnakeCase = (str) => str
    .replace(/\W+/g, ' ')
    .split(/ |\B(?=[A-Z])/)
    .map((word) => word.toLowerCase())
    .join('_');
// Support for custom tags
const customTagsMap = {
    b: (props) => react_1.default.createElement("strong", Object.assign({}, props)),
    bold: (props) => react_1.default.createElement("strong", Object.assign({}, props))
};
const defaultRichTextElements = {
    b: (chunks) => react_1.default.createElement("strong", null, chunks),
    bold: (chunks) => react_1.default.createElement("strong", null, chunks),
    br: () => react_1.default.createElement("br", null),
    p: (chunks) => react_1.default.createElement("p", null, chunks),
    strong: (chunks) => react_1.default.createElement("strong", null, chunks)
};
/**
 * A function for transforming messages with embedded HTML elements into new messages that preserve the HTML elements.
 *
 * Support for embedded HTML elements was removed from `react-intl` in version 4 in favor of rich text elements. As a result,
 * any attributes attached to an HTML element (such as the `href` attribute on an anchor tag) will be lost. This function
 * aims to restore the original behavior.
 *
 * @example
 * ```tsx
 * const messages = {
 *  'message.test': 'Testing <a href="www.google.com">a link</a>'
 * };
 * const { newMessages, replacements } = transformMessages(messages);
 * console.log(newMessages); // { 'message.test': 'Testing <message_test_1>a link</message_test_1>' }
 * console.log(replacements); // { message_test_1: (chunks) => <a href="www.google.com">{chunks}</a> }
 * ```
 *
 * @param messages Object containing message keys and values
 * @returns new object of messages with rich text elements replaced with placeholders along with replacement values for the placeholders
 */
const transformMessages = (messages = {}) => {
    const newMessages = {};
    const replacements = {};
    Object.keys(messages).forEach((key) => {
        replacements[key] = {};
        const str = messages[key];
        // Sanitize the string to prevent issues like XSS attacks.
        const sanitizedStr = DOMPurify.sanitize(str, {
            // Allow custom tags to be used in the message.
            ADD_TAGS: Object.keys(customTagsMap),
            CUSTOM_ELEMENT_HANDLING: {
                tagNameCheck: (tagName) => Object.keys(customTagsMap).includes(tagName),
                attributeNameCheck: null,
                allowCustomizedBuiltInElements: false // no customized built-ins allowed
            }
        });
        // Parse into a React element to more easily preserve properties.
        const parsedResult = (0, html_react_parser_1.default)(sanitizedStr, {
            // Replace custom tags with desired React elements.
            replace(domNode) {
                if (domNode instanceof html_react_parser_1.Element) {
                    const { name, attribs, children } = domNode;
                    const CustomTag = customTagsMap[name];
                    if (CustomTag) {
                        return (react_1.default.createElement(CustomTag, Object.assign({}, attribs), (0, html_react_parser_1.domToReact)(children)));
                    }
                }
                return;
            }
        });
        // Create a new key in snake case (other cases are not supported by `react-intl`).
        const replacementKey = toSnakeCase(key);
        if (typeof parsedResult === 'string') {
            // If the result is a string, there are no embedded HTML elements
            // to replace, so we can just use the original message.
            newMessages[key] = parsedResult;
        }
        else if (Array.isArray(parsedResult)) {
            // If the result is an array, there are HTML elements embedded in the message.
            let newMessage = ``;
            parsedResult.forEach((parsed, index) => {
                if (parsed.type) {
                    // If the parsed result has a `type`, it is a React element.
                    const newKey = `${replacementKey}_${index}`;
                    const { children } = parsed.props;
                    // Replace the HTML elements in the text with custom rich text element placeholders
                    newMessage = `${newMessage}<${newKey}>${children}</${newKey}>`;
                    // The replacement must be a function that renders any related `chunks` as children (to support placeholders
                    // within the embedded HTML element). We use `cloneElement` to preserve the properties of the HTML element
                    // and replace the `children` with the `chunks`. The replacement function will be applied to the rich text
                    // element placeholder created in the new message above.
                    replacements[key][`${newKey}`] = (chunks) => parsed.props.children != undefined
                        ? (0, react_1.cloneElement)(parsed, { children: chunks })
                        : parsed;
                }
                else {
                    // Otherwise, the parsed result is a string, so we can just append it to the new message.
                    newMessage = `${newMessage}${parsed}`;
                }
            });
            newMessages[key] = newMessage;
        }
        else {
            // If the parsed result has a `type`, it is a React element.
            if (parsedResult.type) {
                const { children } = parsedResult.props;
                // Similar to the above case, only we are dealing with a single element.
                newMessages[key] = `<${replacementKey}>${children}</${replacementKey}>`;
                replacements[key][replacementKey] = (chunks) => parsedResult.props.children != undefined
                    ? (0, react_1.cloneElement)(parsedResult, { children: chunks })
                    : parsedResult;
            }
            else {
                // We shouldn't get here, but if we do, we can just use the sanitized original message.
                newMessages[key] = sanitizedStr;
            }
        }
    });
    return { newMessages, replacements };
};
/**
 * A function for configuring `IntlProvider` properties to support embedded HTML elements in messages.
 *
 * Support for embedded HTML elements was removed from `react-intl` in version 4 in favor of rich text elements. As a result,
 * any attributes attached to an HTML element (such as the `href` attribute on an anchor tag) will be lost. This function
 * aims to restore the original behavior by transforming HTML elements in messages into rich text elements and providing
 * appropriate replacements for the rich text elements.
 *
 * @param props `IntlProvider` properties
 * @returns new `IntlProvider` properties with support for embedded HTML elements
 */
const configureEmbeddedHTMLSupport = (props) => {
    const { messages } = props, remainingProps = __rest(props, ["messages"]);
    const { newMessages, replacements } = transformMessages(messages);
    const newProps = Object.assign(Object.assign({}, remainingProps), { defaultRichTextElements, messages: newMessages, values: replacements });
    return newProps;
};
exports.configureEmbeddedHTMLSupport = configureEmbeddedHTMLSupport;
//# sourceMappingURL=util.js.map